import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A reusable Input field component for forms, integrated with react-hook-form.
 * Displays validation errors and supports all standard input types.
 * Uses semantic color classes from shared configuration.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - The field label text
 * @param {string} props.name - The field name (used for form registration and error display)
 * @param {Function} props.register - The react-hook-form register function
 * @param {Object} [props.rules] - Validation rules for react-hook-form
 * @param {Object} props.errors - The errors object from react-hook-form
 * @param {string} [props.type='text'] - HTML input type attribute
 * @param {boolean} [props.required=false] - Whether the field is required (shows red asterisk)
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.helpText] - Help text displayed below the input
 * @param {string} [props.className] - Additional CSS classes
 */
const InputField = ({ 
    label, 
    name, 
    register, 
    rules, 
    errors, 
    type = 'text', 
    required = false,
    placeholder,
    helpText,
    helperText, // Accept helperText as alias for helpText
    className = '',
    ...props 
}) => {
    // Use helpText or helperText (helperText is deprecated, use helpText)
    const displayHelpText = helpText || helperText;
    const colors = useColors();
    const semantic = colors.semantic;
    const { replace } = useTextReplacement();

    const hasError = errors && errors[name];
    const errorMessage = hasError ? errors[name].message : null;

    return (
        <div className={`mb-4 ${className}`}>
            <label htmlFor={name} className={`block text-sm font-medium ${semantic.formLabel} mb-1`}>
                {replace(label)}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
                type={type}
                id={name}
                {...(register ? register(name, rules) : {})}
                className={`w-full px-3 py-2 ${semantic.formBg} ${semantic.formBorder} border rounded-md ${semantic.formText} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    hasError ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                placeholder={placeholder ? replace(placeholder) : undefined}
                aria-invalid={hasError ? 'true' : 'false'}
                aria-describedby={hasError ? `${name}-error` : displayHelpText ? `${name}-help` : null}
                {...props}
            />
            {displayHelpText && !hasError && (
                <p id={`${name}-help`} className={`mt-1 text-xs ${semantic.formLabel}`}>
                    {replace(displayHelpText)}
                </p>
            )}
            {hasError && (
                <p id={`${name}-error`} className="mt-1 text-xs text-red-500" role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

InputField.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    register: PropTypes.func,
    rules: PropTypes.object,
    errors: PropTypes.object,
    type: PropTypes.string,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
    className: PropTypes.string,
};

export default InputField;

